import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";
import type { ServiceRow } from "../production-types";
import { MobileProductionCard } from "./MobileProductionCard";

export type GrupoDeEstagio = {
  /** `null` = "Sem estágio". */
  id: string | null;
  rotulo: string;
  cor?: string | null;
  linhas: ServiceRow[];
};

type Props = {
  grupos: GrupoDeEstagio[];
  podeMover: boolean;
  onAbrir: (row: ServiceRow) => void;
  onMover: (row: ServiceRow) => void;
};

/**
 * Os estágios como acordeões verticais, no celular.
 *
 * O quadro do desktop põe as colunas lado a lado e rola de lado. No celular isso significa que só
 * uma coluna e um naco da seguinte cabem na tela — e arrastar um card pra uma coluna invisível não
 * é gesto que exista. Empilhados, cada estágio mostra nome e quantidade, e abre sob demanda.
 *
 * Um aberto por vez: com oito estágios expandidos a tela vira uma lista sem fim e a contagem, que é
 * a leitura mais útil aqui, se perde. Começa no primeiro que tem trabalho.
 *
 * Ordem, criação, exclusão e renomeação de estágios não passam por aqui — isto é só apresentação.
 */
export function MobileStageAccordion({ grupos, podeMover, onAbrir, onMover }: Props) {
  const primeiroComTrabalho = grupos.find((g) => g.linhas.length > 0);
  const [abertoId, setAbertoId] = useState<string | null | undefined>(
    primeiroComTrabalho ? primeiroComTrabalho.id : undefined,
  );

  return (
    <div className="flex flex-col gap-2">
      {grupos.map((g) => {
        const chave = g.id ?? "__sem_estagio__";
        const aberto = abertoId === g.id;
        return (
          <section
            key={chave}
            className="overflow-hidden rounded-xl border border-zinc-200 bg-white dark:border-border dark:bg-card"
          >
            <h3>
              <button
                type="button"
                onClick={() => setAbertoId(aberto ? undefined : g.id)}
                aria-expanded={aberto}
                className="flex min-h-[52px] w-full items-center gap-3 bg-zinc-50/70 px-4 py-3 text-left transition-colors hover:bg-zinc-100 dark:bg-muted/30 dark:hover:bg-muted/50"
              >
                {g.cor && (
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: g.cor }}
                  />
                )}
                <span className="min-w-0 flex-1 break-words text-[15px] font-bold uppercase tracking-wide text-zinc-700 dark:text-foreground/90">
                  {g.rotulo}
                </span>
                <span className="inline-flex h-6 min-w-[24px] shrink-0 items-center justify-center rounded-full bg-zinc-200 px-1.5 text-xs font-bold tabular-nums text-zinc-600 dark:bg-muted dark:text-muted-foreground">
                  {g.linhas.length}
                </span>
                <ChevronDown
                  className={cn(
                    "h-5 w-5 shrink-0 text-zinc-400 transition-transform",
                    aberto && "rotate-180",
                  )}
                />
              </button>
            </h3>

            {aberto &&
              (g.linhas.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-zinc-500 dark:text-muted-foreground">
                  Nenhum trabalho neste estágio.
                </p>
              ) : (
                <ul className="flex flex-col gap-2 p-2">
                  {g.linhas.map((row) => (
                    <MobileProductionCard
                      key={`${row.order.id}-${row.service?.id ?? "sem-servico"}`}
                      row={row}
                      podeMover={podeMover}
                      onAbrir={() => onAbrir(row)}
                      onMover={() => onMover(row)}
                    />
                  ))}
                </ul>
              ))}
          </section>
        );
      })}
    </div>
  );
}
